import { memo } from 'react';
import type { SvgIconComponent } from '@mui/icons-material';
import { Archive, Check, Checklist, Close, DirectionsRun, Done, ImportExport, WatchLater } from '@mui/icons-material';
import type { CellContext } from '@tanstack/react-table';
import { Loader } from 'components/common';
import { useTranslation } from 'react-i18next';
import { TABLE_CELL_STATUS } from 'types/enums.ts';
import { styled, Typography } from '@mui/material';

const Item = styled('div')(() => ({
  display: 'flex',
  '& > div': {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px',
    borderRadius: '4px',
    borderWidth: '1px',
  },
}));

const Box = (value: string, icon: SvgIconComponent, colors: { bg: `#${string}`; border: `#${string}`; icon?: `#${string}` }) => {
  const { t } = useTranslation();

  const Icon = icon;

  return (
    <Item>
      <div
        style={{
          backgroundColor: colors.bg,
          borderColor: colors.border,
        }}>
        <Icon style={colors.icon ? { color: colors.icon } : { color: colors.border }} />
        <Typography variant="subtitle2">{t(`table.status.${value.toLowerCase()}`) ?? t(`table.status.${value.toLowerCase()}`)}</Typography>
      </div>
    </Item>
  );
};

interface Props<T, P> {
  value: TABLE_CELL_STATUS;
  row: CellContext<T, P>;
  onClick?: (row: T, cell: keyof P) => unknown;
  isClickable?: boolean;
}

const StatusBase = <T, P>({ value, row, onClick }: Props<T, P>) => {
  const component = () => {
    switch (value) {
      case TABLE_CELL_STATUS.ACTIVE:
        return Box('Active', Check, {
          bg: '#ECFAEB',
          border: '#0AC84B',
        });
      case TABLE_CELL_STATUS.ARCHIVED:
        return Box('Archived', Archive, {
          bg: '#D9D9D9',
          border: '#BDBCBC',
          icon: '#000',
        });
      case TABLE_CELL_STATUS.DESIGN:
        return Box('Návrh', Checklist, {
          bg: '#FFF6A1',
          border: '#FFEB34',
          icon: '#000',
        });
      case TABLE_CELL_STATUS.INACTIVE:
        return Box('Inactive', Close, {
          bg: '#FCECEC',
          border: '#FF0000',
        });
      case TABLE_CELL_STATUS.INVITED:
        return Box('Invited', WatchLater, {
          bg: '#FFF6A1',
          border: '#FFEB34',
          icon: '#000',
        });
      case TABLE_CELL_STATUS.DRAFT:
        return Box('Draft', WatchLater, {
          bg: '#FFF6A1',
          border: '#FFEB34',
          icon: '#000',
        });
      case TABLE_CELL_STATUS.ONLINE:
        return Box('Online', Check, {
          bg: '#ECFAEB',
          border: '#0AC84B',
        });
      case TABLE_CELL_STATUS.OFFLINE:
        return Box('Offline', Close, {
          bg: '#FCECEC',
          border: '#FF0000',
        });
      case TABLE_CELL_STATUS.UNKNOWN:
        return Box('Unknown', Close, {
          bg: '#FCECEC',
          border: '#FF0000',
        });
      case TABLE_CELL_STATUS.LOADING:
        return <Loader />;
      case TABLE_CELL_STATUS.NEW:
        return Box('New', ImportExport, {
          bg: '#FFF',
          border: '#3e9623',
        });
      case TABLE_CELL_STATUS.IN_PROCESSS:
        return Box('InProcess', DirectionsRun, {
          bg: '#FFF',
          border: '#0043fd',
        });
      case TABLE_CELL_STATUS.IMPORTED:
        return Box('imported', Done, {
          bg: '#FFF',
          border: '#000',
        });
      case TABLE_CELL_STATUS.SUCCESS:
        return Box('Success', Check, {
          bg: '#ECFAEB',
          border: '#0AC84B',
        });
      case TABLE_CELL_STATUS.FAILED:
        return Box('Failed', Close, {
          bg: '#FCECEC',
          border: '#FF0000',
        });
      case TABLE_CELL_STATUS.CREATED:
        return Box('Created', Done, {
          bg: '#ECFAEB',
          border: '#0AC84B',
        });
      case TABLE_CELL_STATUS.UPDATED:
        return Box('Updated', ImportExport, {
          bg: '#EBF0FA',
          border: '#2C64C8',
        });
      default:
        <></>;
    }
  };

  const handleRowClick = () => {
    if (onClick) {
      onClick(row.row.original, row.column.id as keyof P);
    }
  };

  return <span onClick={handleRowClick}>{component()}</span>;
};

export const Status = memo(StatusBase) as typeof StatusBase;
